// src/controllers/domainController.js
import apiService from '../services/apiService';
import Domain from '../models/Domain';

export const domainController = {
  // Get all domains with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `domains?${queryParams}` : 'domains';
      const response = await apiService.get(endpoint);
      const items = response.data?.map(item => Domain.fromJSON(item)) || [];
      return { success: true, data: items, total: response.total || items.length };
    } catch (error) {
      return { success: false, data: [], message: error.message || 'Failed to fetch domains' };
    }
  },

  // Get single domain by ID
  getById: async (id) => {
    try {
      const response = await apiService.get(`domains/${id}`);
      return { success: true, data: Domain.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Get domain by subdomain name
  getBySubdomain: async (subdomain) => {
    try {
      const response = await apiService.get(`domains/subdomain/${subdomain}`);
      return { success: true, data: Domain.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Check if subdomain is available
  checkAvailability: async (subdomain, domainType = 'subdomain') => {
    try {
      const response = await apiService.post('domains/check-availability', { 
        subdomain, 
        domainType 
      });
      return { 
        success: true, 
        available: response.data.available,
        message: response.data.message 
      };
    } catch (error) {
      return { success: false, available: false, message: error.message };
    }
  },

  // Create new domain
  create: async (data) => {
    try {
      const item = new Domain(data);
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.post('domains', item.toJSON());
      return { 
        success: true, 
        data: Domain.fromJSON(response.data), 
        message: 'Domain created successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update existing domain
  update: async (id, data) => {
    try {
      const item = new Domain({ ...data, id });
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.put(`domains/${id}`, item.toJSON());
      return { 
        success: true, 
        data: Domain.fromJSON(response.data), 
        message: 'Domain updated successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Verify DNS records
  verifyDNS: async (id) => {
    try {
      const response = await apiService.post(`domains/${id}/verify-dns`);
      return { 
        success: true, 
        data: Domain.fromJSON(response.data),
        verified: response.data.dnsVerified,
        message: response.data.dnsVerified 
          ? 'DNS verification successful' 
          : 'DNS verification failed. Please check your DNS records.' 
      };
    } catch (error) {
      return { 
        success: false, 
        verified: false,
        message: error.message || 'DNS verification failed' 
      };
    }
  },

  // Generate SSL certificate
  generateSSL: async (id) => {
    try {
      const response = await apiService.post(`domains/${id}/generate-ssl`);
      return { 
        success: true, 
        data: Domain.fromJSON(response.data),
        message: 'SSL certificate generated successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Renew SSL certificate
  renewSSL: async (id) => {
    try {
      const response = await apiService.post(`domains/${id}/renew-ssl`);
      return { 
        success: true, 
        data: Domain.fromJSON(response.data),
        message: 'SSL certificate renewed successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Toggle domain status (activate/deactivate)
  toggleStatus: async (id) => {
    try {
      const response = await apiService.patch(`domains/${id}/toggle-status`);
      return { 
        success: true, 
        data: Domain.fromJSON(response.data),
        message: response.data.isActive 
          ? 'Domain activated' 
          : 'Domain deactivated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update allowed modules
  updateModules: async (id, modules) => {
    try {
      const response = await apiService.patch(`domains/${id}/modules`, { 
        allowedModules: modules 
      });
      return { 
        success: true, 
        data: Domain.fromJSON(response.data),
        message: 'Allowed modules updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Delete domain
  delete: async (id) => {
    try {
      await apiService.delete(`domains/${id}`);
      return { success: true, message: 'Domain deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk delete domains
  bulkDelete: async (domainIds) => {
    try {
      await apiService.post('domains/bulk-delete', { domainIds });
      return { success: true, message: 'Domains deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk update domains
  bulkUpdate: async (domainIds, updates) => {
    try {
      const response = await apiService.post('domains/bulk-update', { 
        domainIds, 
        updates 
      });
      return { 
        success: true, 
        data: response.data, 
        message: 'Domains updated successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Export domains
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`domains/export?${queryParams}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get domain statistics
  getStatistics: async () => {
    try {
      const response = await apiService.get('domains/statistics');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Get DNS records for domain
  getDNSRecords: async (id) => {
    try {
      const response = await apiService.get(`domains/${id}/dns-records`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Update redirect path
  updateRedirectPath: async (id, redirectPath) => {
    try {
      const response = await apiService.patch(`domains/${id}/redirect`, { 
        redirectPath 
      });
      return { 
        success: true, 
        data: Domain.fromJSON(response.data),
        message: 'Redirect path updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  }
};

export default domainController;